import {
    ref, computed, unref,
} from 'vue';
import type { Ref } from 'vue';
import type { DataTableOptionsType } from '@/types/DataTableOptionsType';
import type { GridConfigType, GridComposableReturnType } from '@/types/composables/GridComposableTypes';
import { useTranslation } from '@/composables';
import { useRouter } from 'vue-router';

export const useGrid = <T extends { id: number | string }>(config: GridConfigType<T>): GridComposableReturnType<T> => {
    const { store, customActions = [], enableSelection = false, idField = 'id', pathRouting } = config;
    const { t } = useTranslation();
    const router = useRouter();

    const isDialogOpen = ref<boolean>(false);
    const editedItemId = ref<T['id'] | null>(null);
    const selectedItems: Ref<T[]> = ref([]);

    const getItemId = (item: T): T['id'] => {
        return item[idField] as T['id'];
    };

    const handleCreate = () => {
        if (pathRouting) {
            const createRouteName = `${pathRouting.baseRouteName}Create`;
            router.push({ name: createRouteName });
        } else {
            editedItemId.value = null;
            isDialogOpen.value = true;
        }
    };

    const handleEdit = (item: T) => {
        const id = getItemId(item);
        if (pathRouting) {
            const editRouteName = `${pathRouting.baseRouteName}Edit`;
            router.push({ name: editRouteName, params: { id: String(id) } });
        } else {
            editedItemId.value = id;
            isDialogOpen.value = true;
        }
    };

    const handleView = (item: T) => {
        const id = getItemId(item);
        if (pathRouting) {
            // Nastav editedItem před navigací, aby useFormDialog použil existující data
            store.editedItem = item;
            const detailRouteName = `${pathRouting.baseRouteName}Detail`;
            router.push({ name: detailRouteName, params: { id: String(id) } });
        } else {
            editedItemId.value = id;
            isDialogOpen.value = true;
        }
    };

    const handleDuplicate = (item: T) => {
        store.prepareItemForDuplication(item);
        if (pathRouting) {
            const createRouteName = `${pathRouting.baseRouteName}Create`;
            router.push({ name: createRouteName });
        } else {
            editedItemId.value = null;
            isDialogOpen.value = true;
        }
    };

    const handleDelete = async (item: T) => {
        return await store.deleteItem(item.id);
    };

    const handleUpdateOptions = async (options: DataTableOptionsType) => {
        await store.fetchAll(options);
    };

    const customActionsConfig = computed(() =>
        customActions.map(({ key, label, icon, color, disabled }) => ({
            key,
            label: t(label),
            icon,
            color,
            disabled: unref(disabled),
        })),
    );

    const handleCustomAction = async (actionKey: string, item: T) => {
        const action = customActions.find(action => action.key === actionKey);
        if (action) {
            await action.handler(item);
        }
    };

    const handleSelectionChange = (selected: T[]) => {
        if (enableSelection) {
            selectedItems.value = selected;
        }
    };

    const clearSelection = () => {
        selectedItems.value = [];
    };

    const isItemSelected = (item: T): boolean => {
        return selectedItems.value.some(selected => selected.id === item.id);
    };

    const hasSelection = computed(() => selectedItems.value.length > 0);
    const selectedCount = computed(() => selectedItems.value.length);

    return {
        isDialogOpen,
        editedItemId,
        selectedItems,
        handleCreate,
        handleEdit,
        handleView,
        handleDuplicate,
        handleDelete,
        handleUpdateOptions,
        handleCustomAction,
        customActionsConfig,
        handleSelectionChange,
        clearSelection,
        isItemSelected,
        hasSelection,
        selectedCount,
    };
};
